import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import AuthService from 'services/AuthService';

const EMAIL_REGEX = /^[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i;

const Signup = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [serverError, setServerError] = useState('');
    const [success, setSuccess] = useState(false);

    const { control, handleSubmit, watch, formState: { errors } } = useForm({
        defaultValues: { username: '', email: '', password: '', confirmPassword: '' },
    });

    const passwordValue = watch('password');

    const onSubmit = async ({ username, email, password }) => {
        setServerError('');
        setIsLoading(true);
        try {
            const response = await AuthService.register(username, password, email);
            if (response?.data) {
                setSuccess(true);
                setTimeout(() => navigate('/login'), 2500);
            } else {
                setServerError(response?.error?.message ?? 'Registration failed. Please try again.');
            }
        } catch (error) {
            setServerError(
                error?.data?.error?.message ||
                error?.message ||
                'Registration failed. Please try again.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.successWrapper}>
                        <CheckCircleOutlineIcon style={styles.successIcon} />
                        <h2 style={styles.successTitle}>Account created!</h2>
                        <p style={styles.successText}>
                            Your account has been created successfully. Redirecting to sign in…
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                {/* Brand */}
                <div style={styles.brand}>
                    <div style={styles.logoMark}>T</div>
                    <span style={styles.brandName}>Trident Algo</span>
                </div>

                <h1 style={styles.title}>Create an account</h1>
                <p style={styles.subtitle}>Start managing your algo trades today</p>

                {serverError && (
                    <div style={styles.errorBanner}>
                        <span style={styles.errorIcon}>⚠</span>
                        {serverError}
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
                    <div style={styles.field}>
                        <label style={styles.label}>Username</label>
                        <Controller
                            name="username"
                            control={control}
                            rules={{
                                required: 'Username is required',
                                minLength: { value: 3, message: 'Minimum 3 characters' },
                                maxLength: { value: 50, message: 'Maximum 50 characters' },
                                pattern: {
                                    value: /^[a-zA-Z0-9_]+$/,
                                    message: 'Only letters, numbers and underscores',
                                },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    placeholder="Choose a username"
                                    error={!!errors.username}
                                    inputProps={{ autoComplete: 'username' }}
                                />
                            )}
                        />
                        {errors.username && (
                            <span style={styles.fieldError}>{errors.username.message}</span>
                        )}
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Email address</label>
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                required: 'Email is required',
                                pattern: { value: EMAIL_REGEX, message: 'Enter a valid email address' },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    type="email"
                                    placeholder="you@example.com"
                                    error={!!errors.email}
                                    inputProps={{ autoComplete: 'email' }}
                                />
                            )}
                        />
                        {errors.email && (
                            <span style={styles.fieldError}>{errors.email.message}</span>
                        )}
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Password</label>
                        <Controller
                            name="password"
                            control={control}
                            rules={{
                                required: 'Password is required',
                                minLength: { value: 8, message: 'Minimum 8 characters' },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Create a strong password"
                                    error={!!errors.password}
                                    inputProps={{ autoComplete: 'new-password' }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword((v) => !v)}
                                                    edge="end"
                                                    size="small"
                                                    tabIndex={-1}
                                                >
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />
                        {errors.password && (
                            <span style={styles.fieldError}>{errors.password.message}</span>
                        )}
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Confirm password</label>
                        <Controller
                            name="confirmPassword"
                            control={control}
                            rules={{
                                required: 'Please confirm your password',
                                validate: (value) =>
                                    value === passwordValue || 'Passwords do not match',
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    type={showConfirm ? 'text' : 'password'}
                                    placeholder="Repeat your password"
                                    error={!!errors.confirmPassword}
                                    inputProps={{ autoComplete: 'new-password' }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirm((v) => !v)}
                                                    edge="end"
                                                    size="small"
                                                    tabIndex={-1}
                                                >
                                                    {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />
                        {errors.confirmPassword && (
                            <span style={styles.fieldError}>{errors.confirmPassword.message}</span>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                            ...styles.submitBtn,
                            ...(isLoading ? styles.submitBtnDisabled : {}),
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={18} style={{ color: '#030712' }} />
                        ) : (
                            'Create Account'
                        )}
                    </button>
                </form>

                <p style={styles.footerText}>
                    Already have an account?{' '}
                    <Link to="/login" style={styles.link}>
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        backgroundColor: '#06090b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        fontFamily: 'Inter, sans-serif',
    },
    card: {
        width: '100%',
        maxWidth: '420px',
        backgroundColor: '#121212',
        border: '1px solid #ffffff0c',
        borderRadius: '16px',
        padding: '2.5rem 2rem',
    },
    brand: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
        marginBottom: '2rem',
    },
    logoMark: {
        width: '36px',
        height: '36px',
        backgroundColor: '#facc15',
        color: '#030712',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '800',
        fontSize: '1.1rem',
        flexShrink: 0,
    },
    brandName: {
        color: '#f3f4f6',
        fontSize: '1.1rem',
        fontWeight: '700',
        letterSpacing: '-0.02em',
    },
    title: {
        color: '#f9fafb',
        fontSize: '1.5rem',
        fontWeight: '700',
        margin: '0 0 0.375rem 0',
        letterSpacing: '-0.02em',
    },
    subtitle: {
        color: '#6b7280',
        fontSize: '0.875rem',
        margin: '0 0 1.75rem 0',
    },
    errorBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: '#1f1010',
        border: '1px solid #7f1d1d',
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        color: '#fca5a5',
        fontSize: '0.875rem',
        marginBottom: '1.25rem',
    },
    errorIcon: {
        fontSize: '1rem',
        flexShrink: 0,
    },
    field: {
        marginBottom: '1.25rem',
    },
    label: {
        display: 'block',
        color: '#d1d5db',
        fontSize: '0.875rem',
        fontWeight: '500',
        marginBottom: '0.4rem',
    },
    fieldError: {
        display: 'block',
        color: '#f87171',
        fontSize: '0.78rem',
        marginTop: '0.3rem',
    },
    submitBtn: {
        width: '100%',
        padding: '0.65rem',
        backgroundColor: '#facc15',
        color: '#030712',
        border: 'none',
        borderRadius: '8px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.9375rem',
        fontWeight: '600',
        cursor: 'pointer',
        marginTop: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        transition: 'background-color 0.15s ease',
        minHeight: '42px',
    },
    submitBtnDisabled: {
        backgroundColor: '#a37f00',
        cursor: 'not-allowed',
    },
    footerText: {
        color: '#6b7280',
        fontSize: '0.875rem',
        textAlign: 'center',
        marginTop: '1.5rem',
        marginBottom: 0,
    },
    link: {
        color: '#facc15',
        textDecoration: 'none',
        fontWeight: '500',
    },
    successWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '1.5rem 0',
        textAlign: 'center',
    },
    successIcon: {
        fontSize: '3.5rem',
        color: '#4ade80',
        marginBottom: '1rem',
    },
    successTitle: {
        color: '#f9fafb',
        fontSize: '1.4rem',
        fontWeight: '700',
        margin: '0 0 0.5rem 0',
    },
    successText: {
        color: '#6b7280',
        fontSize: '0.875rem',
        margin: 0,
    },
};

export default Signup;
